@let isOpen = connectorsEc.runs.selectors.isLauncherOpen.asSignal();
@if (isOpen) {
  @let connectors =
    connectorsEc.plugins.currentListPlugin.feature.selectors.entities.asSignal();
  @let isLoading =
    connectorsEc.plugins.currentListPlugin.feature.selectors.isLoading.asSignal();
  @let loadError =
    connectorsEc.plugins.currentListPlugin.feature.selectors.error.asSignal();
  @let definitions =
    definitionsEc.plugins.currentListPlugin.feature.selectors.entities.asSignal();

  <atn-ds-centered-overlay type="wide">
    <atn-ds-widget title="Run integrations">
      <i
        actions
        class="atn-icon atn-close atn-red"
        (click)="
          connectorsEc.runs.actions.closeLauncher
            .from('IntegrationRun.Dialog.Close.Clicked')
            .dispatch()
        "
      ></i>

      <div class="atn-integration-run-dialog__content">
        <div class="atn-integration-run-dialog__intro">
          Run a connector and monitor each imported API path independently.
        </div>

        @if (loadError) {
          <div
            class="atn-integration-run-dialog__message atn-integration-run-dialog__message--error"
          >
            <div>
              <b>Connectors could not be loaded</b>
              <span>Try loading the available integrations again.</span>
            </div>
            <atn-ds-button
              (click)="
                connectorsEc.plugins.currentListPlugin.feature.actions.load
                  .from('IntegrationRun.Dialog.Connectors.Refetched')
                  .dispatch()
              "
            >
              Retry
            </atn-ds-button>
          </div>
        } @else if (isLoading) {
          <div class="atn-integration-run-dialog__message">
            Loading connectors...
          </div>
        } @else if (connectors.length === 0) {
          <div class="atn-integration-run-dialog__message">
            <div>
              <b>No connectors configured</b>
              <span>
                Add a connector in Admin integrations before running an import.
              </span>
            </div>
          </div>
        } @else {
          @for (connector of connectors; track connector.id) {
            @let connectorDefinition =
              getDefinition(definitions, connector.definitionId);
            @let modelIds =
              connectorDefinition
                ? Ents.core.integrations.Connector.Definition.getModelIds(
                    connectorDefinition
                  )
                : [];
            @let truncateModelIds =
              connectorsEc.runs.selectors
                .truncateModelIds(connector.id)
                .asSignal();
            @let entry =
              connectorsEc.runs.selectors.entry(connector.id).asSignal();
            @let lastRun = entry?.run ?? connector.lastRun;
            @let runStatus =
              lastRun
                ? lastRun.error
                  ? Ents.core.integrations.Connector.Run.Status.Failed
                  : Ents.core.integrations.Connector.Run.Status.fromImports(
                      lastRun.imports
                    )
                : undefined;
            @let isRunning =
              entry?.loading ||
              runStatus === Ents.core.integrations.Connector.Run.Status.Running;

            <div class="atn-integration-run-dialog__connector">
              <div class="atn-integration-run-dialog__connector-header">
                <div class="atn-integration-run-dialog__identity">
                  <div class="atn-integration-run-dialog__name-row">
                    <b class="atn-integration-run-dialog__name">
                      {{ connector.name }}
                    </b>
                    @if (runStatus) {
                      <span
                        class="atn-integration-run-dialog__badge atn-integration-run-dialog__badge--{{
                          runStatus
                        }}"
                      >
                        {{ runStatus }}
                      </span>
                    }
                  </div>
                  <span class="atn-integration-run-dialog__definition">
                    {{ connectorDefinition?.name ?? connector.definitionId }}
                  </span>
                  <small class="atn-integration-run-dialog__endpoint">
                    {{ connector.baseUrl }}
                  </small>
                </div>

                <atn-ds-button
                  [disabled]="isRunning"
                  (click)="
                    connectorsEc.runs.actions.run.request
                      .from('IntegrationRun.Dialog.Run.Clicked')
                      .dispatch({
                        connectorId: connector.id,
                        truncateModelIds,
                      })
                  "
                >
                  @if (isRunning) {
                    Running...
                  } @else if (entry?.error) {
                    Retry
                  } @else if (lastRun) {
                    Run again
                  } @else {
                    Run
                  }
                </atn-ds-button>
              </div>

              @if (modelIds.length > 0) {
                <div class="atn-integration-run-dialog__existing-data">
                  <div class="atn-integration-run-dialog__existing-data-header">
                    <b>Existing data</b>
                    <span>
                      Select models whose existing records should be removed
                      before this import.
                    </span>
                  </div>
                  <div class="atn-integration-run-dialog__models">
                    @for (modelId of modelIds; track modelId) {
                      <atn-ds-checkbox
                        [disabled]="isRunning"
                        [label]="modelId"
                        [selected]="truncateModelIds.includes(modelId)"
                        (selectedChange)="
                          connectorsEc.runs.actions.setTruncation
                            .from(
                              'IntegrationRun.Dialog.ModelTruncation.Changed'
                            )
                            .dispatch({
                              connectorId: connector.id,
                              modelId,
                              selected: $event,
                            })
                        "
                      />
                    }
                  </div>
                  @if (truncateModelIds.length > 0) {
                    <div class="atn-integration-run-dialog__truncate-warning">
                      Existing records in the selected models will be
                      permanently removed before the import starts.
                    </div>
                  }
                </div>
              }

              @if (entry?.error; as runError) {
                <div class="atn-integration-run-dialog__request-error">
                  <span>{{ runError.message }}</span>
                  <button
                    type="button"
                    (click)="
                      connectorsEc.runs.actions.dismiss
                        .from('IntegrationRun.Dialog.Result.Dismissed')
                        .dispatch(connector.id)
                    "
                  >
                    Dismiss
                  </button>
                </div>
              }

              <div class="atn-integration-run-dialog__run-details">
                @if (lastRun) {
                  <div class="atn-integration-run-dialog__run-summary">
                    <span>
                      Last run
                      {{ lastRun.startedAt | date: 'MMM d, y, HH:mm' }}
                    </span>
                    <span>
                      {{ lastRun.imports.length }}
                      {{ lastRun.imports.length === 1 ? 'import' : 'imports' }}
                    </span>
                  </div>

                  @if (lastRun.error) {
                    <div class="atn-integration-run-dialog__run-error">
                      {{ lastRun.error }}
                    </div>
                  }

                  <div class="atn-integration-run-dialog__imports">
                    @for (importRun of lastRun.imports; track $index) {
                      <div class="atn-integration-run-dialog__import">
                        <span class="atn-integration-run-dialog__import-path">
                          {{ importRun.path }}
                        </span>
                        <span
                          class="atn-integration-run-dialog__import-status atn-integration-run-dialog__import-status--{{
                            importRun.status
                          }}"
                        >
                          {{ importRun.status }}
                        </span>
                        @if (importRun.error) {
                          <span
                            class="atn-integration-run-dialog__import-error"
                          >
                            {{ importRun.error }}
                          </span>
                        }
                      </div>
                    }
                  </div>
                } @else {
                  <span class="atn-integration-run-dialog__never-run">
                    This connector has not been run yet.
                  </span>
                }
              </div>
            </div>
          }
        }
      </div>
    </atn-ds-widget>
  </atn-ds-centered-overlay>
}
